import React, { useEffect, useState } from "react";
import {
  Modal,
  Table,
  Button,
  Input,
  Select,
  Space,
  Popconfirm,
  message,
} from "antd";
import {
  getAllUserGroups,
  createUserGroup,
  updateUserGroup,
  deleteUserGroup,
} from "../../core/api/auth";
import { all_routes } from "../../Router/all_routes";
import { getPagePermissions } from "../../core/api/auth";
const { Option } = Select;

const selectedKeys = [
  "productlist",
  "marketinglist",
  "orderlist",
  "mediaPlansList",
  "mailFiles",
  "customer",
  "mailingReport",
  "salesByDate",
  "listReport",
  "salesDashboard",
  "dialySalesUpdate",
  "salesDumpAnalysis",
  "validatorMarketingFile",
  "clients",
  "stockData",
  "mailingResponseLTVData",
  "salesDumpData",
];

const PERMISSION_TYPES = ["create", "edit", "delete", "download"];

const permissionLabels = {
  create: "Create",
  edit: "Edit",
  delete: "Delete",
  download: "Download",
};

const PAGE_PERMISSION_LABELS = Object.fromEntries(
  Object.entries(all_routes).map(([key, path]) => [
    path,
    key.charAt(0).toUpperCase() + key.slice(1) + " Page",
  ])
);

const UserGroupManager = ({ visible, onClose }) => {
  const [groups, setGroups] = useState([]);
  const [editingGroup, setEditingGroup] = useState(null);
  const [name, setName] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [pageOptions, setPageOptions] = useState([]);
  const [pageLabels, setPageLabels] = useState({});

  useEffect(() => {
    if (visible) {
      fetchGroups();
      getPagePermissions()
        .then((data) => {
          setPageOptions(data);
          setPageLabels(
            Object.fromEntries(data.map((opt) => [opt.value, opt.label]))
          );
        })
        .catch(() => {
          message.error("Failed to load page permissions.");
        });
    }
  }, [visible]);

  const fetchGroups = async () => {
    try {
      const data = await getAllUserGroups();
      setGroups(data);
    } catch (err) {
      message.error("Failed to load groups.");
    }
  };

  useEffect(() => {
    if (visible) fetchGroups();
  }, [visible]);

  const resetForm = () => {
    setName("");
    setPermissions([]);
    setEditingGroup(null);
  };

  const handleSave = async () => {
    try {
      const payload = {
        name,
        page_permissions: permissions,
      };

      if (editingGroup) {
        await updateUserGroup(editingGroup.id, payload);
        message.success("Group updated.");
      } else {
        await createUserGroup(payload);
        message.success("Group created.");
      }

      resetForm();
      fetchGroups();
    } catch (err) {
      message.error("Save failed.");
    }
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setName(group.name);
    setPermissions(group.page_permissions || []);
  };

  const handleDelete = async (id) => {
    try {
      await deleteUserGroup(id);
      message.success("Deleted.");
      fetchGroups();
    } catch {
      message.error("Delete failed.");
    }
  };

  return (
    <Modal
      open={visible}
      title="Manage User Groups"
      onCancel={() => {
        resetForm();
        onClose();
      }}
      footer={null}
      width={720}
      className="user-group-form"
      zIndex={2000}>
      <Space direction="vertical" style={{ width: "100%" }}>
        <Input
          placeholder="Group Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Select
          mode="multiple"
          placeholder="Select Page Permissions"
          style={{ width: "100%" }}
          value={permissions}
          onChange={(vals) => setPermissions(vals)}
          options={pageOptions}
        />
        <Button type="primary" onClick={handleSave}>
          {editingGroup ? "Update Group" : "Create Group"}
        </Button>

        <Table
          columns={[
            { title: "Name", dataIndex: "name" },
            {
              title: "Pages",
              dataIndex: "page_permissions",
              render: (arr) =>
                arr
                  .map((path) => {
                    const normalized = path
                      .replace(/\/\*$/, "")
                      .replace(/\/$/, "");
                    return pageLabels[normalized] || path;
                  })
                  .join(", "),
            },
            {
              title: "Actions",
              render: (_, group) => (
                <Space>
                  <Button size="small" onClick={() => handleEdit(group)}>
                    Edit
                  </Button>
                  <Popconfirm
                    title="Delete this group?"
                    onConfirm={() => handleDelete(group.id)}>
                    <Button size="small" danger>
                      Delete
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
          dataSource={groups}
          rowKey="id"
          pagination={false}
        />
      </Space>
    </Modal>
  );
};

export default UserGroupManager;
